import React, { useEffect, useLayoutEffect, useRef } from 'react';
import { Link, useLocation, useParams, useSearchParams } from 'react-router-dom';
import {
  Button,
  Center,
  Container,
  Divider,
  Loader,
  Paper,
  Stack,
  Text,
  Title,
  useMantineColorScheme,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconExternalLink, IconMessageChatbot, IconToolsKitchen2 } from '@tabler/icons-react';
import { useMenu } from '../hooks/useMenu';
import { useMenuColorScheme } from '../hooks/useMenuColorScheme';
import { detectBrowserLanguage } from '../utils/browserLanguageDetector';
import { getEffectiveOnboardingContent } from '../components/menu/OnboardingModal';
import { buildAssistantLinks } from '../utils/aiAssistantLinks';
import { buildMenuText } from '../utils/menuToText';
import { t } from '../utils/uiTranslations';
import { setSkipThemePersist } from '../utils/themePersist';
import classes from './ModelSelectLanding.module.css';

/**
 * Experimental "AI assistant picker" landing page.
 *
 * A QR code with `landingView === 'model_select'` resolves here (via MenuAlias)
 * instead of the standard menu view. It explains the menu and lets the diner:
 *   - open it in their own assistant (ChatGPT / Gemini / Claude),
 *   - copy the menu text to paste anywhere,
 *   - use our built-in menu chat, or
 *   - browse the full standard menu.
 */
const ModelSelectLanding: React.FC = () => {
  const { menuId } = useParams<{ menuId: string }>();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const { menu, loading, error } = useMenu(menuId!);

  const langParam = searchParams.get('lang');
  const language = langParam === 'source' ? 'jp' : langParam || detectBrowserLanguage();

  // Apply the restaurant's color scheme (same approach as MenuV2), without persisting it.
  const { colorScheme: menuColorScheme } = useMenuColorScheme(menuId);
  const { setColorScheme } = useMantineColorScheme();
  const appliedSchemeRef = useRef<string | null>(null);
  useLayoutEffect(() => {
    if (!location.pathname.startsWith('/menus/')) return;
    if (appliedSchemeRef.current === menuColorScheme) return;
    setSkipThemePersist(true);
    setColorScheme(menuColorScheme);
    document.documentElement.setAttribute('data-mantine-color-scheme', menuColorScheme);
    setSkipThemePersist(false);
    appliedSchemeRef.current = menuColorScheme;
  }, [menuColorScheme, location.pathname, setColorScheme]);

  useEffect(() => {
    const name = menu?.restaurant?.name;
    document.title = name ? `${name} - Smartmenu` : 'Smartmenu';
    return () => {
      document.title = 'Smartmenu';
    };
  }, [menu?.restaurant?.name]);

  if (loading) {
    return (
      <Center h="100vh">
        <Loader />
      </Center>
    );
  }

  if (error || !menu) {
    return (
      <Center h="100vh" p="md">
        <Paper withBorder p="xl" maw={420}>
          <Stack align="center" gap="sm">
            <Title order={3} ta="center">
              {t('menuNotFound', language)}
            </Title>
            <Text c="dimmed" ta="center">
              {t('qrShareHelp', language)}
            </Text>
          </Stack>
        </Paper>
      </Center>
    );
  }

  const restaurantName = menu.restaurant?.name || t('restaurantFallbackName', language);
  const onboarding = getEffectiveOnboardingContent(menu, language);
  const introTitle = (onboarding.title || '').replace('{restaurantName}', restaurantName);
  const assistantLinks = buildAssistantLinks(menuId!, restaurantName, language);
  const search = location.search;

  const handleCopyMenu = async () => {
    try {
      await navigator.clipboard.writeText(buildMenuText(menu, language));
      notifications.show({ message: t('menuCopied', language), color: 'green' });
    } catch {
      notifications.show({ message: t('menuCopyFailed', language), color: 'red' });
    }
  };

  return (
    <div className={classes.wrapper}>
      <Container size="sm">
        <Stack gap="xl">
          <Stack gap="sm">
            {menu.onboardingModalImageUrl && (
              <img
                className={classes.heroImage}
                src={menu.onboardingModalImageUrl}
                alt={`${restaurantName}`}
              />
            )}
            <Title order={2}>{introTitle || restaurantName}</Title>
            {onboarding.sections?.length ? (
              onboarding.sections.map((section, index) => (
                <Text key={index} c="dimmed">
                  {section.title ? <strong>{section.title} </strong> : null}
                  {section.content}
                </Text>
              ))
            ) : (
              <Text c="dimmed">{t('landingIntroFallback', language)}</Text>
            )}
          </Stack>

          <div className={classes.card}>
            <Stack gap="xs">
              <Title order={4}>{t('openInYourAssistant', language)}</Title>
              <Text size="sm" c="dimmed">
                {t('openInYourAssistantHelp', language)}
              </Text>
              <div className={classes.assistantButtons}>
                {assistantLinks.map((link) => (
                  <Button
                    key={link.provider}
                    component="a"
                    href={link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    variant="default"
                    leftSection={<IconExternalLink size={16} />}
                  >
                    {link.label}
                  </Button>
                ))}
              </div>
              <Button variant="subtle" size="sm" onClick={handleCopyMenu}>
                {t('copyMenu', language)}
              </Button>
            </Stack>
          </div>

          <Divider label={t('landingOtherWays', language)} labelPosition="center" />

          <Stack gap="sm">
            <Button
              component={Link}
              to={`/menus/${menuId}/chat${search}`}
              variant="light"
              leftSection={<IconMessageChatbot size={18} />}
            >
              {t('useBuiltInAssistant', language)}
            </Button>
            <Button
              component={Link}
              to={`/menus/${menuId}${search}`}
              variant="subtle"
              leftSection={<IconToolsKitchen2 size={18} />}
            >
              {t('browseFullMenu', language)}
            </Button>
          </Stack>
        </Stack>
      </Container>
    </div>
  );
};

export default ModelSelectLanding;
